import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, setLanguage } from "i18n";
import { LanguageFlag } from "./flags";

/**
 * LanguageSelect - pick the app language from a list of flags and native
 * language names. Switching applies immediately and is remembered (see
 * setLanguage in src/i18n.js).
 *
 * @param {"inline"|"popover"} placement - "inline" (default) expands the list
 *   below the trigger, pushing content down, so it works inside containers
 *   that clip overflow (e.g. the profile menu). "popover" floats the list over
 *   the page like a regular dropdown (e.g. a settings row).
 * @param {string} className - Additional classes for the wrapper
 */
const LanguageSelect = ({ placement = "inline", className = "" }) => {
  const floating = placement === "popover";
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const listId = useId();

  const currentCode = i18n.resolvedLanguage || i18n.language;
  const current =
    SUPPORTED_LANGUAGES.find((language) => language.code === currentCode) ||
    SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // Close just this list, not an enclosing menu.
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open]);

  const choose = (code) => {
    setLanguage(code);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div
      ref={wrapperRef}
      className={`${floating ? "relative" : ""} ${className}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t("common.language.current", {
          language: current.label,
        })}
        className={`flex w-full items-center gap-2.5 rounded-lg border bg-white px-3 py-2 text-left transition duration-150 hover:bg-[#F9FAFB] ${
          open ? "border-[#E07A3A]" : "border-[#E5E7EB]"
        }`}
      >
        <LanguageFlag code={current.code} />
        <span className="flex-1 truncate text-sm font-medium text-[#111827]">
          {current.label}{" "}
          <span className="font-normal uppercase text-[#9CA3AF]">
            ({current.code})
          </span>
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.15 }}
          className="flex"
        >
          <ChevronDown size={16} className="text-[#9CA3AF]" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            id={listId}
            role="listbox"
            aria-label={t("common.language.label")}
            initial={
              floating ? { opacity: 0, y: -4 } : { height: 0, opacity: 0 }
            }
            animate={
              floating ? { opacity: 1, y: 0 } : { height: "auto", opacity: 1 }
            }
            exit={floating ? { opacity: 0, y: -4 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className={`overflow-hidden rounded-lg border border-[#F3F4F6] bg-white py-1 ${
              floating
                ? "absolute left-0 right-0 top-full z-50 mt-1.5 shadow-lg"
                : "mt-1.5"
            }`}
          >
            {SUPPORTED_LANGUAGES.map(({ code, label }) => {
              const selected = code === current.code;
              return (
                <li key={code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    lang={code}
                    onClick={() => choose(code)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <LanguageFlag code={code} />
                    <span
                      className={`flex-1 text-sm ${
                        selected
                          ? "font-semibold text-[#111827]"
                          : "text-[#374151]"
                      }`}
                    >
                      {label}{" "}
                      <span className="font-normal uppercase text-[#9CA3AF]">
                        ({code})
                      </span>
                    </span>
                    {selected && <Check size={14} className="text-[#E07A3A]" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelect;
